import { useState, useEffect, useCallback } from 'react';
import { useAuthToken } from './useAuthToken';

/**
 * Fetches a Data Insights report, handling the token refresh, the loading and error state every
 * report page needs, and a retry.
 *
 * The last good data stays in place while a new request runs, so a page can dim it instead of
 * blanking. A failed request keeps it too, but pages show only the error then, never stale figures.
 *
 * @param {Function} fetcher - a function returning an axios promise (e.g. () => insightsService.foo(days))
 * @param {Array} deps - re-fetch when any of these change (e.g. [days])
 * @returns {{ data, loading, error, reload }} reload() runs the request again, for a Try again button.
 */
export const useInsightData = (fetcher, deps = []) => {
  const refreshToken = useAuthToken();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        await refreshToken();
        const response = await fetcher();
        if (!cancelled) setData(response.data);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError('Could not load these figures.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, attempt]);

  return { data, loading, error, reload };
};
